import React, { useState, useEffect } from "react";
import baseUrl from "../../../ajax/api";
import { QUESTION_SETS } from "./questions";
import workbrookLogo from "../../../assets/images/workbrook_logo.svg";
import "./CandidateReference.scss";

const CandidateReference = ({ match }) => {
  const token = match?.params?.token;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [prefill, setPrefill] = useState(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Recruitment services opt-in (success page)
  const [wantsRecruitmentInfo, setWantsRecruitmentInfo] = useState(false);
  const [recruitmentEmail, setRecruitmentEmail] = useState("");
  const [recruitmentSubmitting, setRecruitmentSubmitting] = useState(false);
  const [recruitmentMessage, setRecruitmentMessage] = useState(null);

  // Referee info
  const [refereeName, setRefereeName] = useState("");
  const [refereeTitleCompany, setRefereeTitleCompany] = useState("");
  const [refereeEmail, setRefereeEmail] = useState("");
  const [dateOfReference, setDateOfReference] = useState(
    new Date().toISOString().split("T")[0]
  );

  // Responses: { q1: "text...", q2: "text...", q17: "unconditional", q18: 8 }
  const [responses, setResponses] = useState({});
  const [endorsementNotes, setEndorsementNotes] = useState("");

  // ---- Fetch pre-fill data on mount ----
  useEffect(() => {
    const fetchReference = async () => {
      try {
        const res = await fetch(`${baseUrl}/reference/${token}`);
        const data = await res.json();
        if (data.status) {
          setPrefill(data.data);
        } else {
          if (data.already_submitted) {
            setAlreadySubmitted(true);
          }
          setLoadError(data.message || "Unable to load this reference form.");
        }
      } catch {
        setLoadError("Network error. Please check your connection and try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchReference();
  }, [token]);

  const questionnaireType = prefill?.questionnaire_type || "";
  const companyName = prefill?.company_name || "";

  // Replace {company_name} placeholder in question text + section intros
  const applyCompanyName = (qs, name) => {
    if (!name) return qs;
    return qs.map((q) => ({
      ...q,
      text: q.text ? q.text.split("{company_name}").join(name) : q.text,
      sectionIntro: q.sectionIntro
        ? q.sectionIntro.split("{company_name}").join(name)
        : q.sectionIntro,
    }));
  };

  // Build final question list:
  // - If admin edited base questions, use those (with {company_name} replaced)
  // - Otherwise use the standard question set for this type
  // - Then merge any admin-added custom questions
  const questions = (() => {
    const edited = prefill?.edited_questions;
    let base;
    if (edited && edited.length > 0) {
      base = edited.map((eq) => ({
        id: eq.id,
        section: eq.section,
        sectionIntro: eq.sectionIntro || undefined,
        label: eq.label,
        text: eq.text,
        type: eq.type,
        options: eq.options || undefined,
      }));
    } else {
      base = QUESTION_SETS[questionnaireType] || [];
    }
    const withCompany = applyCompanyName(base, companyName);
    var DEFAULT_RADIO_OPTIONS = [
      { value: "unconditional", label: "Yes, without reservation" },
      { value: "conditional", label: "Yes, with minor caveats" },
      { value: "do_not_endorse", label: "No" },
    ];
    const custom = prefill?.custom_questions || [];
    const customWithIds = custom.map((cq, i) => ({
      id: `custom_${i}`,
      section: cq.section,
      label: cq.label,
      text: cq.text ? cq.text.split("{company_name}").join(companyName) : cq.text,
      type: cq.type,
      options: cq.type === "radio" && !cq.options ? DEFAULT_RADIO_OPTIONS : cq.options,
    }));
    return [...withCompany, ...customWithIds];
  })();

  const handleResponseChange = (questionId, value) => {
    setResponses((prev) => ({ ...prev, [questionId]: value }));
  };

  // Group questions by section for rendering
  const sections = questions.reduce((acc, q) => {
    if (!acc[q.section]) acc[q.section] = [];
    acc[q.section].push(q);
    return acc;
  }, {});

  // ---- Validation ----
  const validate = () => {
    if (!refereeName.trim()) return "Please enter your name.";
    if (!refereeTitleCompany.trim()) return "Please enter your title and company.";
    for (const q of questions) {
      const val = responses[q.id];
      if (val === undefined || val === "" || val === null) {
        return `Please answer all questions. Question "${q.text.substring(0, 60)}..." is missing.`;
      }
    }
    return null;
  };

  // ---- Submit ----
  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setError(null);
    setSubmitting(true);

    try {
      const res = await fetch(`${baseUrl}/reference/${token}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          referee_name: refereeName,
          referee_title_company: refereeTitleCompany,
          referee_email: refereeEmail || null,
          date_of_reference: dateOfReference,
          responses,
          endorsement: responses[questions.find((q) => q.type === "radio")?.id] || null,
          endorsement_notes: endorsementNotes || null,
          overall_rating: responses[questions.find((q) => q.type === "rating")?.id] || null,
        }),
      });
      const data = await res.json();
      if (data.status) {
        setSuccess(true);
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setError(data.message || "Submission failed. Please try again.");
      }
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Recruitment services opt-in submit ----
  const handleRecruitmentSubmit = async () => {
    setRecruitmentSubmitting(true);
    setRecruitmentMessage(null);
    try {
      const res = await fetch(`${baseUrl}/reference/${token}/recruitment-interest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          referee_name: refereeName,
          referee_email: recruitmentEmail,
          candidate_name: prefill?.candidate_name || "",
        }),
      });
      const data = await res.json();
      if (data.status) {
        setRecruitmentMessage("Thank you! We'll be in touch soon.");
      } else {
        setRecruitmentMessage(data.message || "Something went wrong. Please try again.");
      }
    } catch {
      setRecruitmentMessage("Network error. Please try again.");
    } finally {
      setRecruitmentSubmitting(false);
    }
  };

  // ---- Loading screen ----
  if (loading) {
    return (
      <div className="cr-page">
        <div className="cr-container">
          <div className="cr-logo">
            <img src={workbrookLogo} alt="Workbrook" />
          </div>
          <div className="cr-loading">
            <div className="cr-spinner" />
            <p>Loading reference questionnaire…</p>
          </div>
        </div>
      </div>
    );
  }

  // ---- Invalid / expired link ----
  if (loadError && !alreadySubmitted) {
    return (
      <div className="cr-page">
        <div className="cr-container">
          <div className="cr-logo">
            <img src={workbrookLogo} alt="Workbrook" />
          </div>
          <div className="cr-error-card">
            <div className="cr-error-icon">!</div>
            <h2>Link Not Found</h2>
            <p>{loadError}</p>
            <p className="cr-error-hint">
              Please check the link you received and try again, or contact the
              person who shared it with you.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ---- Already submitted ----
  if (alreadySubmitted) {
    return (
      <div className="cr-page">
        <div className="cr-container">
          <div className="cr-logo">
            <img src={workbrookLogo} alt="Workbrook" />
          </div>
          <div className="cr-success-card">
            <div className="cr-success-icon" style={{ background: "#f0ad4e" }}>✓</div>
            <h2>Already Submitted</h2>
            <p>
              This reference questionnaire has already been submitted. Thank you
              for your time.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ---- Success screen ----
  if (success) {
    return (
      <div className="cr-page">
        <div className="cr-container">
          <div className="cr-logo">
            <img src={workbrookLogo} alt="Workbrook" />
          </div>
          <div className="cr-success-card">
            <div className="cr-success-icon">✓</div>
            <h2>Thank You!</h2>
            <p>
              Your candidate reference for {prefill?.candidate_name} has been
              submitted successfully. We appreciate your time and thoughtful
              feedback.
            </p>
          </div>

          {/* Recruitment services offer */}
          <div className="cr-recruitment-offer">
            <h3>Need help with your own hiring?</h3>
            <p>
              Thanks for helping us evaluate our candidate! If you ever need
              character references for your own open roles or want end-to-end
              recruitment support, Workbrook is here to help.
            </p>
            <label className="cr-recruitment-check">
              <input
                type="checkbox"
                checked={wantsRecruitmentInfo}
                onChange={(e) => setWantsRecruitmentInfo(e.target.checked)}
              />
              <span>
                I'd like to hear more about Workbrook's recruitment services for
                my team. 
              </span>
            </label>
            {wantsRecruitmentInfo && (
              <div className="cr-recruitment-email">
                <input
                  type="email"
                  placeholder="If different from the email we have"
                  value={recruitmentEmail}
                  onChange={(e) => setRecruitmentEmail(e.target.value)}
                />
                <button
                  type="button"
                  className="cr-recruitment-btn"
                  disabled={recruitmentSubmitting}
                  onClick={handleRecruitmentSubmit}
                >
                  {recruitmentSubmitting ? "Sending…" : "Send me details"}
                </button>
              </div>
            )}
            {recruitmentMessage && (
              <p className="cr-recruitment-message">{recruitmentMessage}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ---- Questionnaire form ----
  return (
    <div className="cr-page">
      <div className="cr-container">
        <div className="cr-logo">
          <img src={workbrookLogo} alt="Workbrook" />
        </div>

        <div className="cr-header">
          <h1>Candidate Reference Questionnaire</h1>
          <p className="cr-subtitle">
            Your responses will be kept confidential and used solely for
            recruitment evaluation purposes.
          </p>
        </div>

        {/* Pre-filled candidate info (read-only) */}
        <div className="cr-prefill-info">
          <div className="cr-prefill-row">
            <span className="cr-prefill-label">Candidate:</span>
            <span className="cr-prefill-value">{prefill?.candidate_name}</span>
          </div>
          {prefill?.target_role && (
            <div className="cr-prefill-row">
              <span className="cr-prefill-label">Target Role:</span>
              <span className="cr-prefill-value">{prefill.target_role}</span>
            </div>
          )}
          {prefill?.company_name && (
            <div className="cr-prefill-row">
              <span className="cr-prefill-label">Company:</span>
              <span className="cr-prefill-value">{prefill.company_name}</span>
            </div>
          )}
          <div className="cr-prefill-row">
            <span className="cr-prefill-label">Questionnaire Type:</span>
            <span className="cr-prefill-value">
              {questionnaireType === "executive" || questionnaireType === "executive_generic"
                ? "Executive / C-Suite"
                : questionnaireType === "non_executive" || questionnaireType === "non_executive_generic"
                ? "Non-Executive"
                : "Unknown"}
            </span>
          </div>
        </div>

        {error && <div className="cr-error-banner">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Referee details */}
          <div className="cr-section">
            <h3 className="cr-section-title">Your Details (Referee)</h3>
            <div className="cr-field-grid">
              <div className="cr-field">
                <label>Your Name *</label>
                <input
                  type="text"
                  value={refereeName}
                  onChange={(e) => setRefereeName(e.target.value)}
                  placeholder="e.g. Jane Smith"
                  required
                />
              </div>
              <div className="cr-field">
                <label>Your Title & Company *</label>
                <input
                  type="text"
                  value={refereeTitleCompany}
                  onChange={(e) => setRefereeTitleCompany(e.target.value)}
                  placeholder="e.g. VP of Marketing, LVMH"
                  required
                />
              </div>
              <div className="cr-field">
                <label>Your Email</label>
                <input
                  type="email"
                  value={refereeEmail}
                  onChange={(e) => setRefereeEmail(e.target.value)}
                  placeholder="optional"
                />
              </div>
              <div className="cr-field">
                <label>Date of Reference</label>
                <input
                  type="date"
                  value={dateOfReference}
                  onChange={(e) => setDateOfReference(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Questions grouped by section */}
          {Object.entries(sections).map(([sectionName, sectionQuestions]) => (
            <div key={sectionName} className="cr-section">
              <h3 className="cr-section-title">{sectionName}</h3>
              {sectionQuestions[0]?.sectionIntro && (
                <p className="cr-section-intro">{sectionQuestions[0].sectionIntro}</p>
              )}
              {sectionQuestions.map((q) => (
                <div key={q.id} className="cr-question">
                  <label className="cr-question-label">
                    <span className="cr-question-tag">{q.label}</span>
                    {q.text}
                  </label>

                  {q.type === "textarea" && (
                    <textarea
                      value={responses[q.id] || ""}
                      onChange={(e) => handleResponseChange(q.id, e.target.value)}
                      rows={4}
                      placeholder="Enter your response…"
                      required
                    />
                  )}

                  {q.type === "radio" && (
                    <div className="cr-radio-group">
                      {q.options.map((opt) => (
                        <label key={opt.value} className="cr-radio-option">
                          <input
                            type="radio"
                            name={q.id}
                            value={opt.value}
                            checked={responses[q.id] === opt.value}
                            onChange={(e) => handleResponseChange(q.id, e.target.value)}
                            required
                          />
                          <span>{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {q.type === "rating" && (
                    <div className="cr-rating-group">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                        <button
                          key={n}
                          type="button"
                          className={`cr-rating-btn ${
                            responses[q.id] === n ? "cr-rating-active" : ""
                          }`}
                          onClick={() => handleResponseChange(q.id, n)}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}

          {/* Endorsement notes (optional, shown for all) */}
          <div className="cr-section">
            <h3 className="cr-section-title">Additional Notes</h3>
            <div className="cr-question">
              <label className="cr-question-label">
                <span className="cr-question-tag">Endorsement Notes</span>
                If you selected "Conditional Endorsement" or have any additional
                context, please explain here.
              </label>
              <textarea
                value={endorsementNotes}
                onChange={(e) => setEndorsementNotes(e.target.value)}
                rows={3}
                placeholder="Optional — add any caveats or context for your endorsement…"
              />
            </div>
          </div>

          {/* Submit */}
          <div className="cr-submit-area">
            <button
              type="submit"
              className="cr-submit-btn"
              disabled={submitting}
            >
              {submitting ? "Submitting…" : "Submit Reference"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CandidateReference;
